import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FaStar } from 'react-icons/fa';
import { FaUserPlus } from 'react-icons/fa6';
import { RiPencilFill } from 'react-icons/ri';
import { BsFillGrid3X3GapFill } from 'react-icons/bs';
import verified_badge from '../../assets/svg/verified_badge.svg';
import link from '../../assets/svg/share_link.svg';
import messageIcon from '../../assets/svg/message.svg';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  InlineTextButton,
  PrimaryButtonInline,
  PrimaryButton,
  ProfilePageSkeleton,
  ProfileListingsSkeleton,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import { addToFollowersList, queryUserListings, removeFromFollowersList } from './ProfilePage.duck';
import { findLikedLists } from '../SearchPage/SearchPage.duck';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { addToLikeList } from '../../ducks/user.duck';
import { trackSellerFollowed as trackGA4SellerFollowed } from '../../util/googleAnalytics';
import BackButton from '../../components/Button/BackButton';
import loadable from '@loadable/component';
import InfiniteScroll from 'react-infinite-scroll-component';
// Import CMSPage directly to avoid loadable chunk delay which causes a white flash
import CMSPageDirect from '../CMSPage/CMSPage';
import CMSPageSkeletonInline from '../../components/CMSPageSkeleton/CMSPageSkeleton';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER),
    [reviews]
  );
  const reviewsOfCustomer = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER),
    [reviews]
  );
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER),
    [reviews]
  );
  const reviewsOfCustomer = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER),
    [reviews]
  );
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;

  const handleProviderClick = useCallback(() => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER), []);
  const handleCustomerClick = useCallback(() => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER), []);

  const desktopReviewTabs = useMemo(
    () => [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider.length }}
            />
          </Heading>
        ),
        selected: isReviewTypeProviderSelected,
        onClick: handleProviderClick,
      },
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer.length }}
            />
          </Heading>
        ),
        selected: isReviewTypeCustomerSelected,
        onClick: handleCustomerClick,
      },
    ],
    [
      reviewsOfProvider.length,
      reviewsOfCustomer.length,
      isReviewTypeProviderSelected,
      isReviewTypeCustomerSelected,
      handleProviderClick,
      handleCustomerClick,
    ]
  );

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    favroiteListingIds,
    // bio,
    // displayName,
    // abbreviatedName,
    isCurrentUser,
    profileUser,
    listings,
    queryListingsError,
    handleFollowerAdd,
    followProgressStatus,
    reviews = [],
    queryReviewsError,
    // publicData,
    // metadata,
    userFieldConfig,
    intl,
    isUserFollowing,
    followerCount,
    likesCount,
    isUserSame,
    // hideReviews,
    history,
    onAddList,
    isEmailVerified,
    totalListingCount,
    totalListingPageCount,
    onFetchListings,
    queryListingsInProgress,
  } = props;
  const route = useRouteConfiguration();
  const [copyStatus, setCopyStatus] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { bio, displayName, publicData, metadata, abbreviatedName } =
    profileUser?.attributes?.profile || {};

  const displayRating = useMemo(() => {
    const formatValue = val => {
      const n = Number(val);
      if (isNaN(n) || n <= 0 || n > 5) return null;
      return n % 1 === 0 ? n.toString() : n.toFixed(2);
    };

    const metaRating = formatValue(metadata?.rating);
    if (metaRating) {
      return metaRating;
    }
    // Safety fallback for legacy corrupted metadata: calculate from loaded reviews
    const providerReviews = (reviews || []).filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
    if (providerReviews.length > 0) {
      const sum = providerReviews.reduce((acc, r) => acc + (r.attributes.rating || 0), 0);
      return formatValue(sum / providerReviews.length);
    }
    return null;
  }, [reviews, metadata?.rating]);

  const displayRatingCount = useMemo(() => {
    let count = metadata?.ratingCount;
    if (count == null || count === 0) {
      const providerReviews = (reviews || []).filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
      count = providerReviews.length;
    }
    if (count >= 1000) {
      let formatted = (count / 1000).toFixed(1);
      formatted = formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
      return `${formatted}k`;
    }
    return count;
  }, [reviews, metadata?.ratingCount]);

  const hasListings = totalListingCount > 0;
  const storeNameNotAvailable = publicData?.profileUrl === undefined;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = useMemo(
    () =>
      richText(bio, {
        linkify: true,
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
      }),
    [bio]
  );

  const listingsContainerClasses = useMemo(
    () =>
      classNames(css.listingsContainer, {
        [css.withBioMissingAbove]: !hasBio,
      }),
    [hasBio]
  );
  const marketPlaceUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;

  const handleCopyToClipboard = useCallback(async () => {
    try {
      if (publicData?.profileUrl) {
        await navigator.clipboard.writeText(
          `${marketPlaceUrl}/${encodeURIComponent(publicData.profileUrl)}`
        );
        setCopyStatus('copied');
      } else {
        throw new Error('Profile URL is not available');
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      setCopyStatus('failed');
    }
    setTimeout(() => {
      setCopyStatus('idle');
    }, 2000);
  }, [publicData?.profileUrl, marketPlaceUrl]);

  const renderCopyStatusMessage = useCallback(() => {
    switch (copyStatus) {
      case 'copied':
        return <FormattedMessage id="ProfilePage.ctaButtonLinkCopySuccess" />;
      case 'failed':
        return <FormattedMessage id="ProfilePage.ctaButtonLinkCopyFailed" />;
      default:
        return <FormattedMessage id="ProfilePage.ctaButtonLinkCopy" />;
    }
  }, [copyStatus]);

  const handleRedirectToProfile = useCallback(() => {
    history.push(createResourceLocatorString('ProfileSettingsPage', route, {}, {}));
  }, [history, route]);

  const handleRedirectToDashboard = useCallback(() => {
    history.push(createResourceLocatorString('BrandManagePage', route, {}, {}));
  }, [history, route]);

  const verifiedMaybe = useMemo(
    () =>
      isEmailVerified ? <img className={css.verify_badgeIcon} src={verified_badge} alt="" /> : null,
    [isEmailVerified]
  );

  const handleFetchMore = useCallback(async () => {
    // Add page count check
    if (isLoading || listings.length >= totalListingCount || page >= totalListingPageCount) {
      return;
    }

    try {
      setIsLoading(true);
      const nextPage = page + 1;
      await onFetchListings(profileUser.id, nextPage, 20, true);
      setPage(nextPage);
    } catch (error) {
      console.error('Error fetching more listings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    listings.length,
    totalListingCount,
    page,
    totalListingPageCount,
    onFetchListings,
    profileUser.id,
  ]);

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  return (
    <div>
      {/* <div className={css.profileHeader}>
        <div className={css.headerContainer}>
          <div>
            <img className={css.avatar} src={props.user?.attributes?.profile?.avatarUrl} alt="" />
          </div>
          <div className={css.profileDetails}>
              <H2 as="h1" className={css.desktopHeading}>
                <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
              </H2>
              <div></div>
          </div>
        </div>
      </div> */}

      <div className={css.profileHeader}>
        <div className={css.backBtnContainer}>
          <BackButton />
        </div>
        <div className={classNames(css.headerContainer, css.hideForMobile)}>
          <div className={css.avatarContainer}>
            <AvatarLarge disableProfileLink className={css.avatar} user={profileUser} />
          </div>
          <div className={css.profileDetails}>
            <div className={css.detailsTop}>
              <H2 as="h1" className={css.desktopHeading}>
                {intl
                  .formatMessage(
                    { id: 'ProfilePage.desktopHeading' },
                    { name: publicData?.rawProfileUrl }
                  )
                  .toUpperCase()}
                {verifiedMaybe}
              </H2>
              {displayRating && displayRatingCount > 0 && (
                <div className={css.rating}>
                  <span className={css.ratingValue}>
                    <span>{displayRating}</span>
                    <FaStar />
                  </span>
                  <span className={css.ratingCount}>({displayRatingCount})</span>
                </div>
              )}
            </div>
            <div className={css.stats}>
              <p>
                <strong>{totalListingCount}</strong>{' '}
                <FormattedMessage id="ProfilePage.headingStatListings" />
              </p>
              <p>
                <strong>{likesCount}</strong> <FormattedMessage id="ProfilePage.headingStatLikes" />
              </p>
              <p>
                <strong>{followerCount}</strong>{' '}
                <FormattedMessage id="ProfilePage.headingStatFollowers" />
              </p>
            </div>
            <p className={css.description}>{bio}</p>
            <div className={css.actions}>
              {isUserSame ? (
                <PrimaryButtonInline
                  onClick={handleRedirectToProfile}
                  rootClassName={classNames(css.accentBtn)}
                >
                  <RiPencilFill />
                  <FormattedMessage id="ProfilePage.ctaButtonEditProfile" />
                </PrimaryButtonInline>
              ) : (
                <PrimaryButtonInline
                  inProgress={followProgressStatus}
                  disabled={followProgressStatus}
                  onClick={() => handleFollowerAdd(profileUser?.id?.uuid)}
                  rootClassName={classNames(css.accentBtn, {
                    [css.disabled]: isUserSame,
                  })}
                >
                  {/* <BsPersonFillAdd /> */}
                  <FaUserPlus />
                  {isUserFollowing ? (
                    <FormattedMessage id="ProfilePage.ctaButtonFollowing" />
                  ) : (
                    <FormattedMessage id="ProfilePage.ctaButtonFollow" />
                  )}
                </PrimaryButtonInline>
              )}
              <span
                className={css.copyTooltop}
                title={
                  storeNameNotAvailable && isUserSame
                    ? intl.formatMessage({ id: 'ProfilePage.clipboardTooltip' })
                    : ''
                }
              >
                <PrimaryButtonInline
                  onClick={handleCopyToClipboard}
                  disabled={storeNameNotAvailable}
                  rootClassName={classNames(css.accentBtn, css.accentBtnCopy, {
                    [css.copyError]: copyStatus === 'failed',
                    [css.disabled]: storeNameNotAvailable,
                  })}
                >
                  <img src={link} alt="" />
                  {renderCopyStatusMessage()}
                </PrimaryButtonInline>
              </span>

              {isUserSame ? (
                <span
                  className={css.tooltipWrapper}
                  data-tooltip={intl.formatMessage({
                    id: 'ProfilePage.dashboardComingSoonTooltip',
                  })}
                >
                  <PrimaryButtonInline
                    disabled
                    // onClick={handleRedirectToDashboard}
                    rootClassName={classNames(
                      css.accentBtn,
                      css.chat,
                      css.dashboard,
                      css.accentBtnCopy,
                      css.disabled
                    )}
                  >
                    <BsFillGrid3X3GapFill />
                    <FormattedMessage id="ProfilePage.ctaButtonDashboard" />
                  </PrimaryButtonInline>
                </span>
              ) : (
                <span
                  className={css.tooltipWrapper}
                  data-tooltip={intl.formatMessage({
                    id: 'ProfilePage.dashboardComingSoonTooltip',
                  })}
                >
                  <PrimaryButtonInline
                    disabled
                    rootClassName={classNames(
                      css.accentBtn,
                      css.chat,
                      css.accentBtnCopy,
                      css.disabled
                    )}
                  >
                    <img src={messageIcon} alt="" />
                    <FormattedMessage id="ProfilePage.ctaButtonChatToSeller" />
                  </PrimaryButtonInline>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={classNames(css.headerContainer, css.forMobile, css.mobileHeaderContainer)}>
          <div className={css.avatarContainer}>
            <AvatarLarge disableProfileLink className={css.avatar} user={profileUser} />
          </div>
          <div className={css.stats}>
            <p>
              <strong>{totalListingCount}</strong>{' '}
              <FormattedMessage id="ProfilePage.headingStatListings" />
            </p>
            <p>
              <strong>{likesCount}</strong> <FormattedMessage id="ProfilePage.headingStatLikes" />
            </p>
            <p>
              <strong>{followerCount}</strong>{' '}
              <FormattedMessage id="ProfilePage.headingStatFollowers" />
            </p>
          </div>
          <div className={css.mobileProfileDetails}>
            <div className={css.detailsTop}>
              <H2 as="h1" className={css.desktopHeading}>
                {/* <FormattedMessage
                  id="ProfilePage.desktopHeading"
                  values={{ name: publicData?.profileUrl }}
                /> */}
                <span>
                  {/* {publicData?.profileUrl || ''} */}
                  {intl
                    .formatMessage(
                      { id: 'ProfilePage.desktopHeading' },
                      { name: publicData?.rawProfileUrl }
                    )
                    .toUpperCase()}
                  {verifiedMaybe}
                </span>
              </H2>
              {displayRating && displayRatingCount > 0 && (
                <div className={css.rating}>
                  <span className={css.ratingValue}>
                    <span>{displayRating}</span>
                    <FaStar />
                  </span>
                  <span className={css.ratingCount}>({displayRatingCount})</span>
                </div>
              )}
            </div>
            <p className={css.description}>{bio}</p>
            <div className={classNames(css.actions, { [css.showChat]: !isUserSame })}>
              {isUserSame ? (
                <PrimaryButtonInline
                  onClick={handleRedirectToProfile}
                  rootClassName={classNames(css.accentBtn)}
                >
                  <RiPencilFill />
                  <FormattedMessage id="ProfilePage.ctaButtonEditProfile" />
                </PrimaryButtonInline>
              ) : (
                <PrimaryButtonInline
                  inProgress={followProgressStatus}
                  disabled={followProgressStatus}
                  onClick={() => handleFollowerAdd(profileUser?.id?.uuid)}
                  rootClassName={classNames(css.accentBtn, {
                    [css.disabled]: isUserSame,
                  })}
                >
                  <FaUserPlus />
                  {isUserFollowing ? (
                    <FormattedMessage id="ProfilePage.ctaButtonFollowing" />
                  ) : (
                    <FormattedMessage id="ProfilePage.ctaButtonFollow" />
                  )}
                </PrimaryButtonInline>
              )}
              <span
                className={css.copyTooltop}
                title={
                  storeNameNotAvailable && isCurrentUser
                    ? intl.formatMessage({ id: 'ProfilePage.clipboardTooltip' })
                    : ''
                }
              >
                <PrimaryButtonInline
                  onClick={handleCopyToClipboard}
                  disabled={storeNameNotAvailable}
                  rootClassName={classNames(css.accentBtn, css.accentBtnCopy, {
                    [css.copyError]: copyStatus === 'failed',
                    [css.disabled]: storeNameNotAvailable,
                  })}
                >
                  <img src={link} alt="" />
                  {renderCopyStatusMessage()}
                </PrimaryButtonInline>
              </span>
              {/* <PrimaryButtonInline rootClassName={classNames(css.accentBtn, css.chat)}>
                <FormattedMessage id="ProfilePage.ctaButtonChatToSeller" />
              </PrimaryButtonInline> */}
              {isUserSame ? (
                <span
                  className={classNames(css.tooltipWrapper, css.dashboard)}
                  data-tooltip={intl.formatMessage({
                    id: 'ProfilePage.dashboardComingSoonTooltip',
                  })}
                >
                  <PrimaryButtonInline
                    disabled
                    onClick={handleRedirectToDashboard}
                    rootClassName={classNames(
                      css.accentBtn,
                      css.chat,
                      css.dashboard,
                      css.accentBtnCopy,
                      css.disabled
                    )}
                  >
                    <BsFillGrid3X3GapFill />
                    <FormattedMessage id="ProfilePage.ctaButtonDashboard" />
                  </PrimaryButtonInline>
                </span>
              ) : (
                <span
                  className={classNames(css.tooltipWrapper)}
                  data-tooltip={intl.formatMessage({
                    id: 'ProfilePage.dashboardComingSoonTooltip',
                  })}
                >
                  <PrimaryButtonInline
                    disabled
                    rootClassName={classNames(
                      css.accentBtn,
                      css.chat,
                      css.accentBtnCopy,
                      css.disabled
                    )}
                  >
                    <img src={messageIcon} alt="" />
                    <FormattedMessage id="ProfilePage.ctaButtonChatToSeller" />
                  </PrimaryButtonInline>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null} */}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {queryListingsInProgress ? (
        <ProfileListingsSkeleton />
      ) : hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage
              id="ProfilePage.listingsTitle"
              values={{ count: totalListingCount }}
            />
          </H4>
          <InfiniteScroll
            className={css.infiniteScroll}
            dataLength={listings.length}
            next={handleFetchMore}
            hasMore={page < totalListingPageCount}
            loader={
              <div className={css.messagePanel}>
                <H4 as="h2" className={css.heading}>
                  Loading...
                </H4>
              </div>
            }
          >
            <ul className={css.listings}>
              {listings.map(l => {
                const isLiked = favroiteListingIds && favroiteListingIds?.has(l.id.uuid);
                return (
                  <li className={css.listing} key={l.id.uuid}>
                    <ListingCard
                      history={history}
                      listing={l}
                      isLiked={isLiked}
                      showAuthorInfo={false}
                      showRating={false}
                      onAddList={onAddList}
                    />
                  </li>
                );
              })}
            </ul>
          </InfiniteScroll>
        </div>
      ) : isUserSame ? (
        <div>
          <PrimaryButton
            onClick={() =>
              history.push(createResourceLocatorString('NewListingPage', route, {}, {}))
            }
            className={css.newProfileBtn}
          >
            {intl.formatMessage({ id: 'ProfilePage.newListingCta' }).toUpperCase()}
          </PrimaryButton>
        </div>
      ) : null}
      {/* {hideReviews ? null : isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      )} */}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    addFollowInProgress,
    addFollowError,
    onHandleAddFollower,
    onRemoveFollower,
    favroiteListingIds,
    userListingRefs,
    onFindLikedLists,
    ...rest
  } = props;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (userListingRefs?.length > 0) {
      const getId = userListingRefs.map(e => e.id);
      onFindLikedLists(getId);
    }
  }, [userListingRefs, onFindLikedLists]);

  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const routes = useRouteConfiguration();
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { followerCount, likesCount, showProfile, cmsload, slug, totalListingCount } = rest;
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;
  const isUserSame = user?.id?.uuid === currentUser?.id?.uuid;


  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
      ? currentUser != null || userShowError != null
      : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // Define all hooks before any early returns to comply with Rules of Hooks
  const handleFollow = useCallback(
    followId => {
      if (!currentUser) {
        rest.history.push(createResourceLocatorString('LoginPage', routes, {}, {}));
        return;
      }
      if (rest.isUserFollowing) {
        onRemoveFollower(followId);
      } else {
        onHandleAddFollower(followId);
        trackGA4SellerFollowed(profileUser || user || { id: { uuid: followId } }, currentUser?.id?.uuid);
      }
    },
    [
      currentUser,
      rest.isUserFollowing,
      rest.history,
      routes,
      onRemoveFollower,
      onHandleAddFollower,
      profileUser,
      user,
    ]
  );

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // While fetchCMSMaybe is resolving, show CMS skeleton to avoid white flash
  // if (cmsload) return <CMSPageSkeletonInline />;

  if (!showProfile) return <CMSPageDirect pageId={slug} />;

  if (!isDataLoaded) {
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
      >
        <LayoutSideNavigation
          sideNavClassName={classNames(css.aside, { [css.profilePage]: true })}
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
          rootClassName={css.profilePageContainer}
          mainColumnClassName={css.mainColumnContainer}
        >
          <ProfilePageSkeleton />
        </LayoutSideNavigation>
      </Page>
    );
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }
  // This is rendering normal profile page (not preview for pending-approval)

  // useEffect(() => {
  //   if (userListingRefs.length > 0) {
  //     const getId = props.userListingRefs?.map(e => e.id);
  //     onFindLikedLists(getId);
  //   }
  // }, [props.userListingRefs]);

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
        mainEntity: {
          '@type': 'Organization',
          name: displayName,
        },
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={classNames(css.aside, { [css.profilePage]: true })}
        topbar={<TopbarContainer />}
        // sideNav={
        //   <AsideContent
        //     user={profileUser}
        //     showLinkToProfileSettingsPage={mounted && isCurrentUser}
        //     displayName={displayName}
        //   />
        // }
        footer={<FooterContainer />}
        rootClassName={css.profilePageContainer}
        mainColumnClassName={css.mainColumnContainer}
      >
        <MainContent
          // bio={bio}
          // displayName={displayName}
          favroiteListingIds={favroiteListingIds}
          profileUser={profileUser}
          isCurrentUser={isCurrentUser}
          // abbreviatedName={abbreviatedName}
          userShowError={userShowError}
          followProgressStatus={addFollowInProgress}
          followerCount={followerCount}
          totalListingCount={totalListingCount}
          likesCount={likesCount}
          isUserSame={isUserSame}
          // publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          handleFollowerAdd={handleFollow}
          queryListingsInProgress={rest.queryListingsInProgress}
          intl={intl}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { favroiteListingIds } = state.SearchPage;
  const {
    userId,
    userShowError,
    queryListingsError,
    queryListingsInProgress,
    userListingRefs,
    reviews = [],
    queryReviewsError,
    addFollowInProgress,
    addFollowError,
    isUserFollowing,
    followerCount,
    likesCount,
    isEmailVerified,
    cmsload,
    showProfile,
    slug,
    totalListingCount,
    totalListingPageCount,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    queryListingsInProgress,
    userListingRefs,
    listings: userListingRefs,
    reviews,
    queryReviewsError,
    addFollowInProgress,
    addFollowError,
    isUserFollowing,
    followerCount,
    likesCount,
    favroiteListingIds,
    isEmailVerified,
    showProfile,
    cmsload,
    slug,
    totalListingCount,
    totalListingPageCount,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { config } = ownProps;
  return {
    onHandleAddFollower: followerId => dispatch(addToFollowersList(followerId)),
    onRemoveFollower: followerId => dispatch(removeFromFollowersList(followerId)),
    onFindLikedLists: listingIds => dispatch(findLikedLists(listingIds)),
    onAddList: (listingId, authorId) => dispatch(addToLikeList(listingId, authorId)),
    onFetchListings: (userId, page = 1, perPage = 20, append = false) =>
      dispatch(queryUserListings(userId, config, false, page, perPage, append)),
  };
};

const ProfilePage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(ProfilePageComponent);

export default ProfilePage;
